import type { BoxProps } from '@mui/material/Box';

import { varFade, MotionViewport } from '@/components/animate';
import { Carousel, useCarousel, CarouselArrowFloatButtons } from '@/components/carousel';
import { Iconify } from '@/components/iconify';
import { Image } from '@/components/image';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { m } from 'framer-motion';
import { TeamSection } from '@/types/sections/TeamSection';
import { Person } from '@/types';

// ----------------------------------------------------------------------

type TeamProps = BoxProps & {
  section: TeamSection;
};

export default function Team({ sx, section, ...other }: TeamProps) {
  const carousel = useCarousel({
    align: 'start',
    slideSpacing: '24px',
    slidesToShow: {
      xs: 1,
      sm: 2,
      md: 3,
      lg: 4,
    },
  });

  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}>
      <Container component={MotionViewport} sx={{ textAlign: 'center', py: { xs: 10, md: 15 } }}>
        <m.div variants={varFade('inDown')}>
          <Typography variant="overline" sx={{ color: 'text.disabled' }}>
            {section?.small}
          </Typography>
        </m.div>

        <m.div variants={varFade('inUp')}>
          <Typography variant="h2" sx={{ my: 3 }}>
            {section?.title}
          </Typography>
        </m.div>

        <m.div variants={varFade('inUp')}>
          <Typography sx={{ mx: 'auto', maxWidth: 640, color: 'text.secondary' }}>
            {section?.subtitle}
          </Typography>
        </m.div>

        <Box sx={{ position: 'relative' }}>
          <CarouselArrowFloatButtons {...carousel.arrows} options={carousel.options} />

          <Carousel carousel={carousel} sx={{ px: 0.5 }}>
            {section?.team?.map((member, index) => (
              <Box
                key={member.name}
                component={m.div}
                variants={varFade('in')}
                sx={{ py: { xs: 8, md: 10 } }}>
                <MemberCard member={member} />
              </Box>
            ))}
          </Carousel>
        </Box>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

type MemberCardProps = {
  member: Person;
};

function MemberCard({ member }: MemberCardProps) {
  return (
    <Card>
      <Typography variant="subtitle1" sx={{ mt: 2.5, mb: 0.5 }}>
        {member.name}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2.5, color: 'text.secondary' }}>
        {member.role}
      </Typography>

      <Box sx={{ px: 1 }}>
        {member.image?.url ? (
          <Image
            alt={member.name}
            src={member.image.url}
            ratio="1/1"
            sx={{ borderRadius: 2 }}
          />
        ) : (
          <Image alt={member.name} ratio="1/1" sx={{ borderRadius: 2 }} />
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {member.email ? (
          <IconButton key="email" href={member.email}>
            <Iconify icon="solar:letter-bold" />
          </IconButton>
        ) : null}
        {member.social?.twitter ? (
          <IconButton key="twitter" href={member.social.twitter}>
            <Iconify icon="socials:twitter" />
          </IconButton>
        ) : null}
        {member.social?.facebook ? (
          <IconButton key="facebook" href={member.social.facebook}>
            <Iconify icon="socials:facebook" />
          </IconButton>
        ) : null}
        {member.social?.instagram ? (
          <IconButton key="instagram" href={member.social.instagram}>
            <Iconify icon="socials:instagram" />
          </IconButton>
        ) : null}
        {member.social?.linkedin && (
          <IconButton key="linkedin" href={member.social.linkedin}>
            <Iconify icon="socials:linkedin" />
          </IconButton>
        )}
      </Box>
    </Card>
  );
}
